/**
 * VedangJyotisham V6.2.0 — Muhurta Phase 19 Combination & Resolution Engine
 * Deterministically resolves multiple simultaneous Muhurta factors into an explainable final result.
 */

export interface FactorEvidence {
  factorId: string;
  factorName: string;
  classification: 'SUPPORT' | 'WARNING' | 'HARD_EXCLUSION' | 'INFORMATIONAL' | 'NEUTRAL';
  scoreImpact: number;
  description: string;
}

export interface ResolvedMuhurtaResult {
  finalClassification: 'SUPPORT' | 'WARNING' | 'HARD_EXCLUSION' | 'INFORMATIONAL' | 'NEUTRAL';
  netScore: number;
  summary: string;
  contributingFactors: FactorEvidence[];
}

/**
 * Combines multiple factor evidences and determines the final resolved Muhurta outcome.
 */
export function resolveMuhurtaFactors(factors: FactorEvidence[]): ResolvedMuhurtaResult {
  if (!factors || factors.length === 0) {
    return {
      finalClassification: 'NEUTRAL',
      netScore: 0,
      summary: 'No Muhurta factors evaluated.',
      contributingFactors: []
    };
  }

  let hasExclusion = false;
  let hasWarning = false;
  let hasSupport = false;
  let netScore = 0;

  for (const f of factors) {
    netScore += f.scoreImpact;
    if (f.classification === 'HARD_EXCLUSION') {
      hasExclusion = true;
    } else if (f.classification === 'WARNING') {
      hasWarning = true;
    } else if (f.classification === 'SUPPORT') {
      hasSupport = true;
    }
  }

  let finalClassification: ResolvedMuhurtaResult['finalClassification'] = 'INFORMATIONAL';
  let summary = '';

  if (hasExclusion) {
    finalClassification = 'HARD_EXCLUSION';
    summary = '⛔ Window rejected due to one or more hard classical exclusions.';
  } else if (hasWarning) {
    finalClassification = 'WARNING';
    summary = '⚠️ Window contains conditional warnings or mixed factors requiring caution/remediation.';
  } else if (hasSupport) {
    finalClassification = 'SUPPORT';
    summary = '✅ Window supported by favorable classical alignments.';
  } else {
    finalClassification = 'NEUTRAL';
    summary = 'ℹ️ Window is neutral or informational.';
  }

  return {
    finalClassification,
    netScore,
    summary,
    contributingFactors: factors
  };
}
